import { useState, useEffect } from 'react';
import { songsApi } from '@/services/api/songs';
import { imageBlobCache } from '@/services/cache/imageBlobCache';

interface UseCachedImageResult {
  imageUrl: string | null;
  isLoading: boolean;
  hasError: boolean;
}

// Track in-flight requests to prevent duplicate API calls
const inFlightRequests = new Map<string, Promise<string>>();

function getCacheKey(songId: number, revisionId?: number): string {
  return revisionId ? `song-${songId}-revision-${revisionId}` : `song-${songId}`;
}

/**
 * Hook to fetch and cache cover art images using IndexedDB for persistent storage
 * @param songId - The song ID
 * @param revisionId - Optional revision ID
 * @param shouldFetch - Whether to fetch the image (default: true)
 * @returns Object containing imageUrl, isLoading, and hasError states
 */
export function useCachedImage(songId: number, revisionId?: number, shouldFetch: boolean = true): UseCachedImageResult {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // If we shouldn't fetch, set loading to false and return early
    if (!shouldFetch) {
      setIsLoading(false);
      setImageUrl(null);
      setHasError(false);
      return;
    }

    let mounted = true;

    async function fetchImage() {
      const key = getCacheKey(songId, revisionId);

      try {
        setIsLoading(true);
        setHasError(false);

        // Check IndexedDB blob cache first
        const cachedUrl = await imageBlobCache.get(songId, revisionId);
        if (cachedUrl) {
          if (mounted) {
            setImageUrl(cachedUrl);
            setIsLoading(false);
          }
          return;
        }

        // Check if there's already an in-flight request for this image
        let requestPromise = inFlightRequests.get(key);

        if (!requestPromise) {
          // Create new request and store it
          requestPromise = (async () => {
            // Get presigned URL from API
            const response = await songsApi.getCoverArtUrl(songId, {
              revisionId,
              expiration: 3600, // 1 hour
            });

            // Fetch the actual image blob from the presigned URL
            const imageResponse = await fetch(response.coverArtUrl);
            if (!imageResponse.ok) {
              throw new Error(`Failed to fetch image: ${imageResponse.status}`);
            }

            const blob = await imageResponse.blob();

            // Store blob in IndexedDB cache (1 week expiration)
            const objectUrl = await imageBlobCache.set(songId, blob, revisionId, 168);

            // Remove from in-flight requests
            inFlightRequests.delete(key);

            return objectUrl;
          })().catch(error => {
            // Remove from in-flight requests on error
            inFlightRequests.delete(key);
            throw error;
          });

          inFlightRequests.set(key, requestPromise);
        }

        // Wait for the request (either new or existing)
        const url = await requestPromise;

        if (mounted) {
          setImageUrl(url);
          setIsLoading(false);
        }
      } catch (error) {
        if (mounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    }

    fetchImage();

    return () => {
      mounted = false;
    };
  }, [songId, revisionId, shouldFetch]);

  return { imageUrl, isLoading, hasError };
}
